import React, { useState, useMemo } from 'react'
import { scaleLinear, scaleBand, scaleOrdinal } from 'd3-scale'
import { interpolateRdYlGn } from 'd3-scale-chromatic'

// Color scheme matching the main app
const COLORS = {
  connectivity: '#dc3545',  // Red
  diversity: '#0dcaf0',     // Cyan
  comfort: '#198754',       // Green
  iqup: '#6c757d'          // Gray
}

const LABELS = {
  connectivity: 'Conectividade',
  diversity: 'Diversidade',
  comfort: 'Conforto'
}

// Histogram component
const Histogram = ({ data, width = 500, height = 280 }) => {
  const margin = { top: 20, right: 30, bottom: 45, left: 45 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  const bins = useMemo(() => {
    const binCount = 10
    const minVal = 0
    const maxVal = 10
    const binWidth = (maxVal - minVal) / binCount

    const histogram = Array(binCount).fill(0).map((_, i) => ({
      x0: minVal + i * binWidth,
      x1: minVal + (i + 1) * binWidth,
      count: 0,
      polos: []
    }))

    data.forEach(polo => {
      const value = polo.indicadores.Valor
      const binIndex = Math.min(Math.floor((value - minVal) / binWidth), binCount - 1)
      if (binIndex >= 0 && binIndex < binCount) {
        histogram[binIndex].count++
        histogram[binIndex].polos.push(polo.nome)
      }
    })

    return histogram
  }, [data])

  const maxCount = Math.max(...bins.map(b => b.count), 1)

  const xScale = scaleLinear()
    .domain([0, 10])
    .range([0, innerWidth])

  const yScale = scaleLinear()
    .domain([0, maxCount])
    .range([innerHeight, 0])

  const [hoveredBin, setHoveredBin] = useState(null)

  return (
    <div className="relative">
      <svg width={width} height={height}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Y axis */}
          <line x1={0} y1={0} x2={0} y2={innerHeight} stroke="#ccc" />
          {yScale.ticks(5).map(tick => (
            <g key={tick} transform={`translate(0,${yScale(tick)})`}>
              <line x1={-5} x2={0} stroke="#ccc" />
              <text x={-10} y={4} textAnchor="end" fontSize="11" fill="#666">
                {tick}
              </text>
            </g>
          ))}
          <text
            transform={`translate(-32,${innerHeight / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize="11"
            fill="#666"
          >
            Qtd. Polos
          </text>

          {/* X axis */}
          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#ccc" />
          {[0, 2, 4, 6, 8, 10].map(tick => (
            <g key={tick} transform={`translate(${xScale(tick)},${innerHeight})`}>
              <line y1={0} y2={5} stroke="#ccc" />
              <text y={18} textAnchor="middle" fontSize="11" fill="#666">
                {tick}
              </text>
            </g>
          ))}
          <text
            x={innerWidth / 2}
            y={innerHeight + 38}
            textAnchor="middle"
            fontSize="11"
            fill="#666"
          >
            IQUP Score
          </text>

          {/* Bars */}
          {bins.map((bin, i) => {
            const barWidth = (innerWidth / bins.length) - 2
            const barHeight = innerHeight - yScale(bin.count)
            const x = xScale(bin.x0) + 1
            const y = yScale(bin.count)
            const colorValue = (bin.x0 + bin.x1) / 2 / 10

            return (
              <g key={i}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={interpolateRdYlGn(colorValue)}
                  stroke={hoveredBin === i ? '#333' : 'white'}
                  strokeWidth={hoveredBin === i ? 2 : 1}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredBin(i)}
                  onMouseLeave={() => setHoveredBin(null)}
                />
                {bin.count > 0 && barHeight > 15 && (
                  <text
                    x={x + barWidth / 2}
                    y={y + 14}
                    textAnchor="middle"
                    fill="white"
                    fontSize="11"
                    fontWeight="bold"
                  >
                    {bin.count}
                  </text>
                )}
              </g>
            )
          })}
        </g>
      </svg>

      {/* Tooltip */}
      {hoveredBin !== null && bins[hoveredBin].count > 0 && (
        <div
          className="absolute bg-gray-900 text-white p-2 rounded shadow-lg"
          style={{
            top: margin.top,
            left: margin.left + xScale(bins[hoveredBin].x0) + 30,
            maxWidth: 200,
            fontSize: '0.75rem',
            zIndex: 100,
            pointerEvents: 'none'
          }}
        >
          <div className="font-bold mb-1">
            IQUP {bins[hoveredBin].x0.toFixed(1)} - {bins[hoveredBin].x1.toFixed(1)}
          </div>
          <div className="text-gray-400 mb-1">{bins[hoveredBin].count} polo(s)</div>
          <div style={{ maxHeight: 100, overflow: 'auto' }}>
            {bins[hoveredBin].polos.slice(0, 5).map((nome, i) => (
              <div key={i}>{nome}</div>
            ))}
            {bins[hoveredBin].polos.length > 5 && (
              <div className="text-gray-400">+{bins[hoveredBin].polos.length - 5} mais...</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Stacked Bar Chart component
const StackedBarChart = ({ data, width = 550, height = 400, config }) => {
  const margin = { top: 10, right: 100, bottom: 40, left: 130 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  // Get weight distribution for proportional stacking
  const weights = useMemo(() => {
    const total = config.connectivity.weight + config.diversity.weight + config.comfort.weight
    return {
      connectivity: config.connectivity.weight / total,
      diversity: config.diversity.weight / total,
      comfort: config.comfort.weight / total
    }
  }, [config])

  // Sort by IQUP and take top 15
  const sortedData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.indicadores.Valor - a.indicadores.Valor)
      .slice(0, 15)
  }, [data])

  const yScale = scaleBand()
    .domain(sortedData.map(d => d.nome))
    .range([0, innerHeight])
    .padding(0.2)

  const xScale = scaleLinear()
    .domain([0, 10])
    .range([0, innerWidth])

  const [hoveredPolo, setHoveredPolo] = useState(null)

  return (
    <div className="relative">
      <svg width={width} height={height}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* X axis */}
          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#ccc" />
          {[0, 2, 4, 6, 8, 10].map(tick => (
            <g key={tick} transform={`translate(${xScale(tick)},${innerHeight})`}>
              <line y1={0} y2={5} stroke="#ccc" />
              <text y={18} textAnchor="middle" fontSize="11" fill="#666">
                {tick}
              </text>
            </g>
          ))}
          <text
            x={innerWidth / 2}
            y={innerHeight + 35}
            textAnchor="middle"
            fontSize="11"
            fill="#666"
          >
            IQUP (contribuição por dimensão)
          </text>

          {/* Grid lines */}
          {[2, 4, 6, 8].map(tick => (
            <line
              key={tick}
              x1={xScale(tick)}
              y1={0}
              x2={xScale(tick)}
              y2={innerHeight}
              stroke="#eee"
              strokeDasharray="3,3"
            />
          ))}

          {/* Bars */}
          {sortedData.map((polo, i) => {
            const { Conectividade, Diversidade, Conforto } = polo.indicadores.Componentes
            const barHeight = yScale.bandwidth()
            const y = yScale(polo.nome)

            // Calculate weighted contributions to IQUP
            const connContrib = Conectividade.Valor * weights.connectivity
            const divContrib = Diversidade.Valor * weights.diversity
            const comfContrib = Conforto.Valor * weights.comfort

            const isHovered = hoveredPolo === polo.id

            return (
              <g
                key={polo.id}
                onMouseEnter={() => setHoveredPolo(polo.id)}
                onMouseLeave={() => setHoveredPolo(null)}
                style={{ cursor: 'pointer' }}
              >
                {/* Polo name */}
                <text
                  x={-8}
                  y={y + barHeight / 2 + 4}
                  textAnchor="end"
                  fontSize="10"
                  fill={isHovered ? '#333' : '#666'}
                  fontWeight={isHovered ? 'bold' : 'normal'}
                >
                  {polo.nome.length > 18 ? polo.nome.slice(0, 16) + '...' : polo.nome}
                </text>

                {/* Connectivity segment */}
                <rect
                  x={0}
                  y={y}
                  width={xScale(connContrib)}
                  height={barHeight}
                  fill={COLORS.connectivity}
                  stroke={isHovered ? '#333' : 'white'}
                  strokeWidth={isHovered ? 2 : 1}
                />

                {/* Diversity segment */}
                <rect
                  x={xScale(connContrib)}
                  y={y}
                  width={xScale(divContrib)}
                  height={barHeight}
                  fill={COLORS.diversity}
                  stroke={isHovered ? '#333' : 'white'}
                  strokeWidth={isHovered ? 2 : 1}
                />

                {/* Comfort segment */}
                <rect
                  x={xScale(connContrib + divContrib)}
                  y={y}
                  width={xScale(comfContrib)}
                  height={barHeight}
                  fill={COLORS.comfort}
                  stroke={isHovered ? '#333' : 'white'}
                  strokeWidth={isHovered ? 2 : 1}
                />

                {/* IQUP value label */}
                <text
                  x={xScale(polo.indicadores.Valor) + 5}
                  y={y + barHeight / 2 + 4}
                  fontSize="10"
                  fontWeight="bold"
                  fill="#333"
                >
                  {polo.indicadores.Valor.toFixed(2)}
                </text>
              </g>
            )
          })}

          {/* Legend */}
          <g transform={`translate(${innerWidth + 8}, 0)`}>
            {Object.entries(COLORS).filter(([k]) => k !== 'iqup').map(([key, color], i) => (
              <g key={key} transform={`translate(0, ${i * 20})`}>
                <rect width={12} height={12} fill={color} />
                <text x={16} y={10} fontSize="10" fill="#666">
                  {LABELS[key]}
                </text>
              </g>
            ))}
          </g>
        </g>
      </svg>

      {/* Tooltip */}
      {hoveredPolo !== null && (() => {
        const polo = sortedData.find(p => p.id === hoveredPolo)
        if (!polo) return null
        const { Conectividade, Diversidade, Conforto } = polo.indicadores.Componentes
        return (
          <div
            className="absolute bg-gray-900 text-white p-2 rounded shadow-lg"
            style={{
              top: margin.top + yScale(polo.nome),
              left: margin.left + xScale(polo.indicadores.Valor) + 50,
              fontSize: '0.7rem',
              zIndex: 100,
              pointerEvents: 'none'
            }}
          >
            <div className="font-bold mb-1">{polo.nome}</div>
            <div>IQUP: <strong>{polo.indicadores.Valor.toFixed(2)}</strong></div>
            <hr className="my-1 border-gray-600" />
            <div style={{ color: COLORS.connectivity }}>
              Conect.: {Conectividade.Valor.toFixed(2)} ({config.connectivity.weight}x)
            </div>
            <div style={{ color: COLORS.diversity }}>
              Divers.: {Diversidade.Valor.toFixed(2)} ({config.diversity.weight}x)
            </div>
            <div style={{ color: COLORS.comfort }}>
              Conforto: {Conforto.Valor.toFixed(2)} ({config.comfort.weight}x)
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// Bump Chart component
const BumpChart = ({ data, width = 700, height = 380 }) => {
  const margin = { top: 35, right: 130, bottom: 20, left: 35 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  const dimensions = ['iqup', 'connectivity', 'diversity', 'comfort']
  const dimensionLabels = {
    iqup: 'IQUP',
    connectivity: 'Conectividade',
    diversity: 'Diversidade',
    comfort: 'Conforto'
  }

  // Calculate rankings for each dimension
  const rankings = useMemo(() => {
    const result = {}

    // IQUP ranking (already sorted)
    const byIqup = [...data].sort((a, b) => b.indicadores.Valor - a.indicadores.Valor)
    result.iqup = new Map(byIqup.map((p, i) => [p.id, i + 1]))

    // Connectivity ranking
    const byConn = [...data].sort((a, b) =>
      b.indicadores.Componentes.Conectividade.Valor - a.indicadores.Componentes.Conectividade.Valor
    )
    result.connectivity = new Map(byConn.map((p, i) => [p.id, i + 1]))

    // Diversity ranking
    const byDiv = [...data].sort((a, b) =>
      b.indicadores.Componentes.Diversidade.Valor - a.indicadores.Componentes.Diversidade.Valor
    )
    result.diversity = new Map(byDiv.map((p, i) => [p.id, i + 1]))

    // Comfort ranking
    const byComf = [...data].sort((a, b) =>
      b.indicadores.Componentes.Conforto.Valor - a.indicadores.Componentes.Conforto.Valor
    )
    result.comfort = new Map(byComf.map((p, i) => [p.id, i + 1]))

    return result
  }, [data])

  // Show top 10 by IQUP
  const topPolos = useMemo(() => {
    return [...data]
      .sort((a, b) => b.indicadores.Valor - a.indicadores.Valor)
      .slice(0, 10)
  }, [data])

  const xScale = scaleBand()
    .domain(dimensions)
    .range([0, innerWidth])
    .padding(0.1)

  const yScale = scaleLinear()
    .domain([1, data.length])
    .range([0, innerHeight])

  // Color scale for lines
  const colorScale = scaleOrdinal()
    .domain(topPolos.map(p => p.id))
    .range([
      '#e41a1c', '#377eb8', '#4daf4a', '#984ea3', '#ff7f00',
      '#a65628', '#f781bf', '#999999', '#66c2a5', '#fc8d62'
    ])

  const [hoveredPolo, setHoveredPolo] = useState(null)

  return (
    <div className="relative">
      <svg width={width} height={height}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Column headers */}
          {dimensions.map(dim => (
            <text
              key={dim}
              x={xScale(dim) + xScale.bandwidth() / 2}
              y={-12}
              textAnchor="middle"
              fontWeight="bold"
              fontSize="11"
              fill={COLORS[dim] || '#333'}
            >
              {dimensionLabels[dim]}
            </text>
          ))}

          {/* Vertical lines for each dimension */}
          {dimensions.map(dim => (
            <line
              key={dim}
              x1={xScale(dim) + xScale.bandwidth() / 2}
              y1={0}
              x2={xScale(dim) + xScale.bandwidth() / 2}
              y2={innerHeight}
              stroke="#ddd"
              strokeWidth={2}
            />
          ))}

          {/* Rank numbers on left */}
          {Array.from({ length: Math.min(data.length, 12) }, (_, i) => i + 1).map(rank => (
            <text
              key={rank}
              x={-8}
              y={yScale(rank) + 4}
              textAnchor="end"
              fontSize="9"
              fill="#999"
            >
              {rank}
            </text>
          ))}

          {/* Lines for each polo */}
          {topPolos.map(polo => {
            const points = dimensions.map(dim => ({
              x: xScale(dim) + xScale.bandwidth() / 2,
              y: yScale(rankings[dim].get(polo.id))
            }))

            const pathD = points
              .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
              .join(' ')

            const isHovered = hoveredPolo === polo.id
            const opacity = hoveredPolo === null ? 0.7 : (isHovered ? 1 : 0.15)

            return (
              <g
                key={polo.id}
                onMouseEnter={() => setHoveredPolo(polo.id)}
                onMouseLeave={() => setHoveredPolo(null)}
                style={{ cursor: 'pointer' }}
              >
                <path
                  d={pathD}
                  fill="none"
                  stroke={colorScale(polo.id)}
                  strokeWidth={isHovered ? 3 : 2}
                  opacity={opacity}
                />
                {points.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={isHovered ? 5 : 4}
                    fill={colorScale(polo.id)}
                    stroke="white"
                    strokeWidth={2}
                    opacity={opacity}
                  />
                ))}
              </g>
            )
          })}

          {/* Legend on right */}
          <g transform={`translate(${innerWidth + 15}, 0)`}>
            {topPolos.map((polo, i) => {
              const isHovered = hoveredPolo === polo.id
              return (
                <g
                  key={polo.id}
                  transform={`translate(0, ${i * 18})`}
                  onMouseEnter={() => setHoveredPolo(polo.id)}
                  onMouseLeave={() => setHoveredPolo(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    x={0}
                    y={-5}
                    width={10}
                    height={10}
                    fill={colorScale(polo.id)}
                    rx={2}
                  />
                  <text
                    x={14}
                    y={4}
                    fontSize="9"
                    fill={isHovered ? '#333' : '#666'}
                    fontWeight={isHovered ? 'bold' : 'normal'}
                  >
                    {polo.nome.length > 14 ? polo.nome.slice(0, 12) + '...' : polo.nome}
                  </text>
                </g>
              )
            })}
          </g>
        </g>
      </svg>

      {/* Tooltip */}
      {hoveredPolo !== null && (() => {
        const polo = topPolos.find(p => p.id === hoveredPolo)
        if (!polo) return null
        return (
          <div
            className="absolute bg-gray-900 text-white p-2 rounded shadow-lg"
            style={{
              top: margin.top + yScale(rankings.iqup.get(polo.id)) - 50,
              left: margin.left + xScale('iqup') + xScale.bandwidth() / 2 + 15,
              fontSize: '0.7rem',
              zIndex: 100,
              pointerEvents: 'none',
              minWidth: 150
            }}
          >
            <div className="font-bold mb-1">{polo.nome}</div>
            <table className="w-full">
              <tbody>
                {dimensions.map(dim => (
                  <tr key={dim}>
                    <td style={{ color: COLORS[dim] || '#fff' }}>{dimensionLabels[dim]}</td>
                    <td className="text-right">#{rankings[dim].get(polo.id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })()}
    </div>
  )
}

// Main Graficos component - all charts on one page
export default function Graficos({ calculatedPolos, nivelFilter, config }) {
  // Filter by nivel
  const filteredPolos = useMemo(() => {
    if (nivelFilter.size === 0) return calculatedPolos
    return calculatedPolos.filter(p => nivelFilter.has(p.nivel))
  }, [calculatedPolos, nivelFilter])

  // Stats
  const stats = useMemo(() => {
    const values = filteredPolos.map(p => p.indicadores.Valor)
    if (values.length === 0) return { mean: 0, median: 0, min: 0, max: 0, stdDev: 0, count: 0 }
    const sum = values.reduce((a, b) => a + b, 0)
    const mean = sum / values.length
    const sorted = [...values].sort((a, b) => a - b)
    const median = sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)]
    const min = Math.min(...values)
    const max = Math.max(...values)
    const variance = values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / values.length
    const stdDev = Math.sqrt(variance)

    return { mean, median, min, max, stdDev, count: values.length }
  }, [filteredPolos])

  return (
    <div>
      {/* Stats summary bar */}
      <div className="flex justify-center gap-4 mb-4 py-2 bg-gray-100 rounded">
        <span className="text-sm">
          <i className="fa fa-calculator mr-1 text-gray-500" />
          Média: <strong>{stats.mean.toFixed(2)}</strong>
        </span>
        <span className="text-sm">
          <i className="fa fa-arrows-h mr-1 text-gray-500" />
          Mediana: <strong>{stats.median.toFixed(2)}</strong>
        </span>
        <span className="text-sm">
          <i className="fa fa-signal mr-1 text-gray-500" />
          Desvio: <strong>{stats.stdDev.toFixed(2)}</strong>
        </span>
        <span className="text-sm">
          <i className="fa fa-arrows-v mr-1 text-gray-500" />
          Min/Max: <strong>{stats.min.toFixed(2)}</strong> - <strong>{stats.max.toFixed(2)}</strong>
        </span>
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Histogram */}
        <div>
          <div className="border rounded p-3 h-full">
            <h6 className="mb-3 font-semibold">
              <i className="fa fa-bar-chart mr-2 text-gray-500" />
              Distribuição IQUP
            </h6>
            <div className="flex justify-center">
              <Histogram data={filteredPolos} width={480} height={260} />
            </div>
            <p className="text-gray-500 text-sm text-center mt-2 mb-0">
              Cores: vermelho (baixo) → amarelo → verde (alto)
            </p>
          </div>
        </div>

        {/* Bump Chart */}
        <div>
          <div className="border rounded p-3 h-full">
            <h6 className="mb-3 font-semibold">
              <i className="fa fa-line-chart mr-2 text-gray-500" />
              Variação de Ranking por Dimensão
            </h6>
            <div className="flex justify-center">
              <BumpChart data={filteredPolos} width={520} height={320} />
            </div>
            <p className="text-gray-500 text-sm text-center mt-2 mb-0">
              Linhas cruzando = desempenho inconsistente entre dimensões
            </p>
          </div>
        </div>

        {/* Stacked Bar - full width */}
        <div className="md:col-span-2">
          <div className="border rounded p-3">
            <h6 className="mb-3 font-semibold">
              <i className="fa fa-align-left mr-2 text-gray-500" />
              Composição do IQUP (Top 15)
            </h6>
            <div className="flex justify-center">
              <StackedBarChart
                data={filteredPolos}
                width={900}
                height={420}
                config={config}
              />
            </div>
            <p className="text-gray-500 text-sm text-center mt-2 mb-0">
              Barras mostram contribuição ponderada:
              <span className="ml-2" style={{ color: COLORS.connectivity }}>■ Conectividade ({config.connectivity.weight}x)</span>
              <span className="ml-2" style={{ color: COLORS.diversity }}>■ Diversidade ({config.diversity.weight}x)</span>
              <span className="ml-2" style={{ color: COLORS.comfort }}>■ Conforto ({config.comfort.weight}x)</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
